"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/button/button";
import { Input } from "@/components/input/input";
import EditLineIcon from "remixicon-react/EditLineIcon";
import SaveLineIcon from "remixicon-react/SaveLineIcon";
import CloseLineIcon from "remixicon-react/CloseLineIcon";
import fetcher from "@/lib/fetcher";
import { CompanyUser } from "@/types/user";

interface ProfileFormState {
  firstName: string;
  lastName: string;
  contactNo: string;
}

const ProfileInfo = ({
  user,
  onProfileUpdated,
}: {
  user: CompanyUser | null;
  onProfileUpdated?: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState<ProfileFormState>({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    contactNo: user?.contactNo || "",
  });

  // Sync form when user data loads asynchronously
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        contactNo: user.contactNo || "",
      });
    }
  }, [user]);

  const addressSource =
    user?.role === "companyAdmin" ? user?.company : user?.store;

  const handleEdit = () => {
    // Reset form to latest user data each time edit is opened
    setForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      contactNo: user?.contactNo || "",
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await fetcher.put("company-users/me", {
        body: {
          firstName: form.firstName,
          lastName: form.lastName,
          contactNo: form.contactNo,
          image: user?.image || "",
        },
      });
      onProfileUpdated?.();
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-bg2">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-black">Profile Information</h2>
          <p className="text-gray-500 text-sm mt-1">
            Manage your personal details
          </p>
        </div>

        {isEditing ? (
          <div className="flex gap-2">
            <Button
              size="small"
              color="primary"
              className="!px-4 !py-2 rounded-xl"
              leftIcon={<SaveLineIcon size={18} />}
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
            >
              Save Changes
            </Button>
            <Button
              size="small"
              color="blackOutlined"
              className="!px-4 !py-2 rounded-xl"
              leftIcon={<CloseLineIcon size={18} />}
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="small"
            className="!bg-primary/10 !text-primary hover:!bg-primary/20 !px-4 !py-2 rounded-xl"
            leftIcon={<EditLineIcon size={18} />}
            onClick={handleEdit}
          >
            Edit Profile
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Input
          label="First Name"
          value={form.firstName}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, firstName: e.target.value }))
          }
          disabled={!isEditing}
          fullWidth
        />
        <Input
          label="Last Name"
          value={form.lastName}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, lastName: e.target.value }))
          }
          disabled={!isEditing}
          fullWidth
        />
        <div className="relative">
          <Input
            label="Email Address"
            defaultValue={user?.email || ""}
            disabled
            fullWidth
            rightIcon={
              <span className="bg-gray-200 text-gray-500 text-[10px] uppercase font-bold px-2 py-1 rounded">
                Locked
              </span>
            }
          />
          <p className="text-xs text-gray-400 mt-1 ml-1">
            Email cannot be changed
          </p>
        </div>
        <Input
          label="Mobile Number"
          value={form.contactNo}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, contactNo: e.target.value }))
          }
          placeholder="Not provided"
          disabled={!isEditing}
          fullWidth
        />
      </div>

      <h3 className="text-lg font-bold text-black mb-4">Shipping Address</h3>
      <div className="space-y-6">
        <Input
          label="Street Address"
          defaultValue={addressSource?.streetAddress}
          placeholder="Not provided"
          disabled
          fullWidth
        />
        <div className="grid md:grid-cols-3 gap-6">
          <Input
            label="City"
            defaultValue={addressSource?.city}
            placeholder="Not provided"
            disabled
            fullWidth
          />
          <Input
            label="State/Province"
            defaultValue={addressSource?.stateProvince}
            placeholder="Not provided"
            disabled
            fullWidth
          />
          <Input
            label="Postal Code"
            defaultValue={addressSource?.postalCode}
            placeholder="Not provided"
            disabled
            fullWidth
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileInfo;
